import { type FC, useEffect, useRef, useState } from "react";
import { ArrowRight, Loader2, ShieldCheck, Info } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FileCard } from "@/shared/components/ui/file-card";
import { FieldError } from "@/shared/components/ui/FieldError";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { NoteCard } from "@/shared/components/FlowController/NoteCard";
import DocumentSheet from "@/shared/components/document-viewer/DocumentSheet";
import { supabase } from "@/shared/lib/supabase";
import { uploadDocument } from "@/shared/utils/uploadDocument";
import { usePerson, useUser } from "@/shared/hooks/useAppContext";
import { getFirstName } from "@/shared/utils/names";
import { EventType, trackEvent } from "@/shared/utils/core";
import { getOnboardingFlow } from "@/shared/utils/funnelAnalytics";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { WHY_WE_ASK, WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";
import { IdCheckErrorModal, type IdCheckErrorType } from "./IdCheckErrorModal";

const DOCUMENT_TYPE = "identification_card";

interface StagedFile {
  file: File;
  status: "idle" | "loading" | "complete" | "error";
  progress: number;
  error: string | null;
}

interface ExistingDoc {
  id: number;
  fileName: string;
  createdAt: string;
}

export const UploadId: FC<FlowCustomComponentProps> = ({ onComplete, setAnswer, answers }) => {
  const person = usePerson();
  const user = useUser();
  const estateId = person?.estate_id;
  const userId = user?.user_id;
  const firstName = getFirstName(person?.full_name || "");

  const [stagedFile, setStagedFile] = useState<StagedFile | null>(null);
  const [existingDoc, setExistingDoc] = useState<ExistingDoc | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [idCheckError, setIdCheckError] = useState<string | null>(
    (answers["id-check-error"] as string) ?? null,
  );
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let isActive = true;

    const checkExisting = async () => {
      const { data: docs } = await supabase
        .from("estate_documents")
        .select("id, file_name, created_at")
        .eq("document_type", DOCUMENT_TYPE)
        .eq("uploaded_by_user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (!isActive || !docs || docs.length === 0) return;
      setExistingDoc({
        id: docs[0].id,
        fileName: docs[0].file_name ?? "ID Document",
        createdAt: docs[0].created_at ?? "",
      });
    };

    checkExisting();
    return () => {
      isActive = false;
    };
  }, [userId]);

  const handleFileSelect = (file: File) => {
    setStagedFile({ file, status: "idle", progress: 0, error: null });
    setError(null);
  };

  const handleFileRemove = () => {
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
    setStagedFile(null);
  };

  const handleDeleteExisting = async () => {
    if (!existingDoc) return;
    await supabase.from("estate_documents").delete().eq("id", existingDoc.id);
    setExistingDoc(null);
  };

  const handleSubmit = async () => {
    if (!stagedFile || isUploading || !estateId) return;

    setIsUploading(true);
    setStagedFile((prev) => (prev ? { ...prev, status: "loading" } : prev));

    const interval = setInterval(() => {
      setStagedFile((prev) => {
        if (!prev || prev.status !== "loading") return prev;
        const next = prev.progress >= 90 ? 90 : prev.progress + Math.random() * 5 + 10;
        return { ...prev, progress: next };
      });
    }, 100);
    progressRef.current = interval;

    try {
      const docId = await uploadDocument(stagedFile.file, DOCUMENT_TYPE, estateId);
      clearInterval(interval);
      progressRef.current = null;
      setStagedFile((prev) => (prev ? { ...prev, status: "complete", progress: 100 } : prev));

      setAnswer("id-document-id", String(docId));
      trackEvent(EventType.IDENTITY_DOCUMENT_UPLOADED, {
        flow: getOnboardingFlow(answers),
        estate_id: String(estateId),
      });
      onComplete();
    } catch (err) {
      clearInterval(interval);
      progressRef.current = null;
      setStagedFile((prev) =>
        prev
          ? { ...prev, status: "error", progress: 0, error: "The upload didn't finish. Try again." }
          : prev,
      );
      setIsUploading(false);
      sentryUtils.captureError(err, "ID upload failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    }
  };

  if (existingDoc && !stagedFile) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <ShieldCheck className="h-10 w-10 text-green-700" />
          <h2>Verify your identity with your ID</h2>
          <p className="text-secondary">
            This keeps {firstName}'s estate safe, so only the right people can see it. It's a
            one-time check with your photo ID.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">Your ID</p>
          <FileCard
            fileName={existingDoc.fileName}
            status="complete"
            onRemove={handleDeleteExisting}
            onClick={() => setViewerOpen(true)}
          />
        </div>

        <SecurityNote text="Files are encrypted and stored securely in your Documents." />

        <Button
          variant="brand"
          rounded
          onClick={() => {
            setAnswer("id-document-id", String(existingDoc.id));
            onComplete();
          }}
          className="w-full"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </Button>

        <AcceptedIdTypes />

        {estateId && (
          <DocumentSheet
            isOpen={viewerOpen}
            setIsOpen={setViewerOpen}
            documentId={existingDoc.id}
            documentName={existingDoc.fileName}
            documentCreatedAt={new Date(existingDoc.createdAt)}
            estateId={estateId}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <ShieldCheck className="h-10 w-10 text-green-700" />
        <h2>
          Verify your identity with your ID
          <TooltipProvider>
            <Tooltip>
              <TooltipInfoTrigger aria-label={WHY_WE_ASK_TITLE} className="ml-1.5 align-middle" />
              <TooltipContent title={WHY_WE_ASK_TITLE}>{WHY_WE_ASK.photoId}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h2>
        <p className="text-secondary">
          This keeps {firstName}'s estate safe, so only the right people can see it. It's a one-time
          check with your photo ID.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">Your ID</p>
        {stagedFile ? (
          <FileCard
            fileName={stagedFile.file.name}
            fileSize={stagedFile.file.size}
            onRemove={handleFileRemove}
            status={stagedFile.status}
            progress={stagedFile.progress}
            errorMessage={stagedFile.error ?? undefined}
          />
        ) : (
          <UploadZone file={null} onFileSelect={handleFileSelect} onFileRemove={() => {}} />
        )}
      </div>

      <SecurityNote text="Files are encrypted and stored securely in your Documents." />

      {error && <FieldError message={error} />}

      <Button
        variant="brand"
        rounded
        onClick={handleSubmit}
        disabled={!stagedFile || isUploading || stagedFile.status === "complete"}
        className="w-full"
      >
        {isUploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            Continue <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>

      <AcceptedIdTypes />

      <IdCheckErrorModal
        open={!!idCheckError}
        type={idCheckError as IdCheckErrorType | null}
        onClose={() => setIdCheckError(null)}
      />
    </div>
  );
};

const AcceptedIdTypes: FC = () => (
  <NoteCard
    variant="info-box"
    cardIcon={Info}
    cardTitle="Accepted IDs"
    cardSubtitle={
      <ul>
        <li>Driver's license</li>
        <li>State ID card</li>
        <li>U.S. passport</li>
      </ul>
    }
    className="mt-0"
  />
);
